import styled, { css } from 'styled-components'
import tinycolor from 'tinycolor2'
import {
  SCROLL_FADE_HEIGHT_CATALOG,
  SCROLL_FADE_HEIGHT_GRID,
  scrollFadeDarkDown,
} from './bulkOrderScrollFade.styles'

const gridBorderColor = ({ theme }) => theme.colors.gray3

const sphColumnMinWidth = '6.2rem'
const cornerHeaderMinHeight = '3.8rem'

const stickyHeaderBase = css`
  position: relative;
  border: none;
  background-clip: padding-box;
  padding: 0.6rem 0.8rem;
  white-space: nowrap;
  transform: translateZ(0);
`

const stickyHeaderBorder = sides => css`
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    box-shadow: ${sides};
  }
`

const gridBorderRight = css`
  inset -1px 0 0 ${gridBorderColor}
`

const gridBorderBottom = css`
  inset 0 -1px 0 ${gridBorderColor}
`

const gridBorderLeft = css`
  inset 1px 0 0 ${gridBorderColor}
`

const gridBorderTop = css`
  inset 0 1px 0 ${gridBorderColor}
`

const gridCellBorder = css`
  border: none;
  border-right: 1px solid ${gridBorderColor};
  border-bottom: 1px solid ${gridBorderColor};
`

const gridStickyHeaderScrollFade = css`
  ${({ $showScrollFade }) => css`
    overflow: visible;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: -${SCROLL_FADE_HEIGHT_CATALOG};
      height: ${SCROLL_FADE_HEIGHT_CATALOG};
      z-index: 1;
      pointer-events: none;
      transform: scaleY(${$showScrollFade ? 1 : 0});
      transform-origin: top center;
      opacity: ${$showScrollFade ? 1 : 0};
      visibility: ${$showScrollFade ? 'visible' : 'hidden'};
      transition: transform 0.2s ease, opacity 0.2s ease, visibility 0.2s ease;
      ${scrollFadeDarkDown}

      @media (prefers-reduced-motion: reduce) {
        transition: none;
      }
    }
  `}
`

export const GridTable = styled.table`
  border-collapse: separate;
  border-spacing: 0;
  font-size: 1.2rem;
  --grid-qty-bg: ${({ theme }) =>
    tinycolor(theme.colors.primaryVeryLight).setAlpha(0.2).toString()};
`

export const CornerHeader = styled.th`
  ${stickyHeaderBase}
  position: sticky;
  left: 0;
  top: 0;
  z-index: 3;
  padding: 0;
  vertical-align: top;
  background: ${({ theme }) => theme.colors.gray2};
  ${stickyHeaderBorder(
    css`
      ${gridBorderRight}, ${gridBorderBottom}, ${gridBorderLeft}, ${gridBorderTop}
    `,
  )}
  ${gridStickyHeaderScrollFade}
`

export const CornerHeaderInner = styled.div`
  position: relative;
  box-sizing: border-box;
  width: ${sphColumnMinWidth};
  height: ${cornerHeaderMinHeight};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(
      to top right,
      transparent calc(50% - 0.5px),
      ${gridBorderColor} calc(50% - 0.5px),
      ${gridBorderColor} calc(50% + 0.5px),
      transparent calc(50% + 0.5px)
    );
  }
`

export const CornerAxisLabel = styled.span`
  ${({ $axis, theme }) => css`
    position: absolute;
    font-size: 1rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    line-height: 1;
    text-transform: uppercase;
    color: ${theme.colors.text};

    ${$axis === 'sph'
      ? css`
          left: 0.7rem;
          bottom: 0.7rem;
        `
      : css`
          right: 0.7rem;
          top: 0.7rem;
        `}
  `}
`

export const CylHeader = styled.th`
  ${({ theme, $isHighlighted }) => css`
    ${stickyHeaderBase}
    position: sticky;
    top: 0;
    z-index: 2;
    min-height: ${cornerHeaderMinHeight};
    box-sizing: border-box;
    background: ${$isHighlighted
      ? theme.button.activeBackgroundColor
      : theme.colors.gray2};
    color: ${$isHighlighted
      ? theme.button.activeTextColor
      : theme.colors.text};
    ${stickyHeaderBorder(css`
      ${gridBorderRight}, ${gridBorderBottom}, ${gridBorderTop}
    `)}
    font-weight: 600;
    text-align: center;
    transition: background-color 0.15s ease, color 0.15s ease;
    ${gridStickyHeaderScrollFade}
  `}
`

export const SphHeader = styled.th`
  ${({ theme, $isHighlighted }) => css`
    ${stickyHeaderBase}
    position: sticky;
    left: 0;
    z-index: 1;
    min-width: ${sphColumnMinWidth};
    background: ${$isHighlighted
      ? theme.button.activeBackgroundColor
      : theme.colors.gray2};
    color: ${$isHighlighted
      ? theme.button.activeTextColor
      : theme.colors.text};
    ${stickyHeaderBorder(css`
      ${gridBorderRight}, ${gridBorderBottom}, ${gridBorderLeft}
    `)}
    font-weight: 600;
    text-align: right;
    transition: background-color 0.15s ease, color 0.15s ease;
  `}
`

export const GridCell = styled.td`
  ${({ theme, $isAvailable, $hasQuantity }) => {
    let background = 'white'
    if (!$isAvailable) {
      background = theme.colors.gray3
    } else if ($hasQuantity) {
      background = 'var(--grid-qty-bg)'
    }

    return css`
      ${gridCellBorder}
      padding: 0;
      position: relative;
      background: ${background};
      scroll-margin-top: calc(
        var(--grid-header-height, ${cornerHeaderMinHeight}) + ${SCROLL_FADE_HEIGHT_CATALOG}
      );
      scroll-margin-left: ${sphColumnMinWidth};
      scroll-margin-bottom: ${SCROLL_FADE_HEIGHT_GRID};
      scroll-margin-right: ${SCROLL_FADE_HEIGHT_GRID};
    `
  }}
`

export const CellInputWrap = styled.div`
  position: relative;
  width: 4.8rem;
  height: 3.2rem;
`

export const CellInput = styled.input`
  ${({ $isAvailable, theme }) => css`
    width: 100%;
    height: 100%;
    border: none;
    background: transparent;
    text-align: center;
    font-size: 1.2rem;
    font-weight: 700;
    color: ${theme.colors.text};
    outline: none;

    ${!$isAvailable &&
    css`
      cursor: not-allowed;
      color: ${theme.colors.text3};
    `}

    &:focus {
      box-shadow: inset 0 0 0 2px ${theme.button.activeBackgroundColor};
    }
  `}
`

export const CellStepper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 1.4rem;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;
  transition-delay: 0s;

  @media (hover: hover) {
    ${GridCell}:hover & {
      opacity: 1;
      pointer-events: auto;
      transition-delay: 0.1s;
    }
  }

  ${GridCell}:focus-within & {
    opacity: 1;
    pointer-events: auto;
    transition-delay: 0s;
  }
`

export const StepButton = styled.button`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    padding: 0;
    border: none;
    background: transparent;
    color: ${theme.colors.text3};
    cursor: pointer;
    line-height: 1;
    opacity: 0.55;
    transition: opacity 0.15s ease, color 0.15s ease;

    @media (hover: hover) {
      &:hover:not(:disabled) {
        opacity: 1;
        color: ${theme.colors.text};
      }
    }

    &:disabled {
      cursor: default;
      opacity: 0.2;
    }
  `}
`
